import { useEffect, useMemo, useState } from "react";
import {
    Box,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ToggleOffOutlined, ToggleOnOutlined } from "@mui/icons-material";
import axios from "axios";
import { toast } from "react-toastify";
import { API } from "../utils/Api";
import Images from "../utils/Images";
import { CategoryFilterChip } from "./FilterChip";

const ProductsListing = () => {
    const token = localStorage.getItem("token");
    const storedOutletData = localStorage.getItem("outletData");
    const outletData = storedOutletData ? JSON.parse(storedOutletData) : null;

    const [loading, setLoading] = useState(false);

    const [categoryList, setCategoryList] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);

    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");

    const [stockFilter, setStockFilter] = useState("All");
    const [statusChip, setStatusChip] = useState("All");
    const [updatingProductId, setUpdatingProductId] = useState(null);

    const [overallCounts, setOverallCounts] = useState({
        total: 0,
        inStock: 0,
        outStock: 0,
    });

    const getProductsList = async () => {
        try {
            setLoading(true);
            const requestUrl = `${API.BASE_URL}masters/category/getCategoryWithProducts/${outletData?._id}`;
            const response = await axios.get(requestUrl, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const cats = response?.data?.data?.categories || [];
            setCategoryList(cats);

            // Flatten categories -> products
            const flat = cats.flatMap((c) =>
                (c.products || []).map((p) => ({
                    ...p,
                    categoryId: c._id,
                    categoryName: c.name,
                }))
            );

            setAllProducts(flat);
            setFilteredProducts(flat);
        } catch (error) {
            console.error("Error:", error);
            toast.error("Failed to fetch products", { autoClose: 2000 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getProductsList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Base list = only event filter (used for overall counts)
        let base = [...allProducts];

        base = base.filter((p) =>
            outletData?.isEventOutlet ? p?.isEventProduct : !p?.isEventProduct
        );

        const total = base.length;
        const outStock = base.filter((p) => !!p?.isExhausted).length;
        const inStock = total - outStock;
        setOverallCounts({ total, inStock, outStock });

        // Now apply UI filters for listing
        let data = [...base];

        // Category filter
        if (selectedCategory !== "All") {
            data = data.filter((p) => p.categoryId === selectedCategory);
        }

        // Status filter: chip overrides dropdown if chip is selected
        const effectiveStockFilter = statusChip !== "All" ? statusChip : stockFilter;

        if (effectiveStockFilter === "InStock") {
            data = data.filter((p) => !p?.isExhausted);
        }
        if (effectiveStockFilter === "Exhausted") {
            data = data.filter((p) => !!p?.isExhausted);
        }

        // Search
        if (searchQuery?.trim()) {
            const q = searchQuery.toLowerCase();
            data = data.filter((p) => (p?.name || "").toLowerCase().includes(q));
        }

        setFilteredProducts(data);
    }, [
        allProducts,
        outletData?.isEventOutlet,
        searchQuery,
        selectedCategory,
        stockFilter,
        statusChip,
    ]);

    const handleSwitchChange = async (productId, isExhausted) => {
        if (updatingProductId) return;

        const url = isExhausted
            ? `${API.BASE_URL}product/exhausted`
            : `${API.BASE_URL}product/undo-exhausted`;
        const method = isExhausted ? "post" : "delete";

        try {
            setUpdatingProductId(productId);
            await axios({
                method,
                url,
                data: { product: productId },
                headers: { Authorization: `Bearer ${token}` },
            });

            // Update master list (filtered list auto updates via effect)
            setAllProducts((prev) =>
                prev.map((p) => (p._id === productId ? { ...p, isExhausted } : p))
            );

            toast.success(
                isExhausted ? "Product set as exhausted!" : "Product set as back in stock!",
                { autoClose: 2000 }
            );
        } catch (error) {
            const errorMessage = error.response?.data?.data?.message || "An error occurred";
            console.error(
                isExhausted ? "Error in setting exhausted:" : "Error in undoing exhausted:",
                errorMessage
            );
            toast.error(errorMessage, { autoClose: 2000 });
        } finally {
            setUpdatingProductId(null);
        }
    };

    const visibleCategories = useMemo(() => {
        return categoryList?.filter((c) =>
            outletData?.isEventOutlet ? c?.isEventCategory : !c?.isEventCategory
        );
    }, [categoryList, outletData?.isEventOutlet]);

    return (
        <Grid item md={12} sx={{ backgroundColor: "#F6F6F6", paddingTop: "20px", paddingX: '10px' }}>
            <Grid container spacing={2} alignItems="center" paddingX={'12px'}>
                {/* Title */}
                <Grid item md={4}>
                    <Typography sx={{ fontSize: "18px", fontWeight: 800 }}>
                        Stock Inventory
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                        Manage product availability quickly
                    </Typography>
                </Grid>

                {/* Search */}
                <Grid item md={8}>
                    <TextField
                        fullWidth
                        sx={{
                            backgroundColor: "#fff",
                            "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                            "& input": { padding: "12px 12px" },
                        }}
                        placeholder="Search product by name"
                        variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </Grid>

                {/* Clickable Overall Counts (do not change on category/search) */}
                <Grid item md={12} display="flex" justifyContent="center">
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>

                        {/* TOTAL */}
                        <Box
                            onClick={() => {
                                setSelectedCategory("All");
                                setStatusChip("All");
                            }}
                            sx={{
                                cursor: "pointer",
                                borderRadius: "12px",
                                padding: "8px 12px",
                                fontSize: "15px",
                                userSelect: "none",
                                transition: "all 0.2s ease",
                                backgroundColor: statusChip === "All" ? "#111827" : "#FFFFFF",
                                color: statusChip === "All" ? "#FFFFFF" : "#374151",
                                border: statusChip === "All"
                                    ? "1px solid #111827"
                                    : "1px solid #E5E7EB",
                            }}
                        >
                            Total: <b>{overallCounts.total}</b>
                        </Box>

                        {/* IN STOCK */}
                        <Box
                            onClick={() => setStatusChip("InStock")}
                            sx={{
                                cursor: "pointer",
                                borderRadius: "12px",
                                padding: "8px 12px",
                                fontSize: "15px",
                                userSelect: "none",
                                transition: "all 0.2s ease",
                                backgroundColor: statusChip === "InStock" ? "#ECFDF5" : "#FFFFFF",
                                color: statusChip === "InStock" ? "#047857" : "#374151",
                                border: statusChip === "InStock"
                                    ? "1px solid #6EE7B7"
                                    : "1px solid #E5E7EB",
                            }}
                        >
                            In Stock: <b>{overallCounts.inStock}</b>
                        </Box>

                        {/* OUT OF STOCK */}
                        <Box
                            onClick={() => {
                                setStatusChip("Exhausted");
                                setSelectedCategory("All");
                            }}
                            sx={{
                                cursor: "pointer",
                                borderRadius: "12px",
                                padding: "8px 12px",
                                fontSize: "15px",
                                userSelect: "none",
                                transition: "all 0.2s ease",
                                backgroundColor: statusChip === "Exhausted" ? "#FEF2F2" : "#FFFFFF",
                                color: statusChip === "Exhausted" ? "#B91C1C" : "#374151",
                                border: statusChip === "Exhausted"
                                    ? "1px solid #FCA5A5"
                                    : "1px solid #E5E7EB",
                            }}
                        >
                            Out of Stock: <b>{overallCounts.outStock}</b>
                        </Box>

                        {/* CLEAR FILTER */}
                        {statusChip !== "All" && (
                            <Box
                                onClick={() => setStatusChip("All")}
                                sx={{
                                    cursor: "pointer",
                                    borderRadius: "12px",
                                    padding: "8px 12px",
                                    fontSize: "15px",
                                    userSelect: "none",
                                    transition: "all 0.2s ease",
                                    backgroundColor: "#F9FAFB",
                                    color: "#374151",
                                    border: "1px dashed #D1D5DB",
                                }}
                            >
                                Clear status filter ✕
                            </Box>
                        )}

                    </Box>
                </Grid>

            </Grid>
            {/* Category chips */}
            <Grid item md={12} sx={{ margin: '15px 0 15px 0' }}>
                <Grid container spacing={1} justifyContent={'center'}>
                    <Grid item xs={2}>
                        <Box
                            onClick={() => {
                                setSelectedCategory("All");
                                setStatusChip("Exhausted");
                            }}
                            sx={{
                                cursor: "pointer",
                                whiteSpace: "nowrap",
                                padding: "8px 14px",
                                borderRadius: "999px",
                                backgroundColor: selectedCategory === "All" ? "#000" : "#fff",
                                color: selectedCategory === "All" ? "#fff" : "#000",
                                border: "1px solid #ddd",
                                fontSize: "13px",
                                userSelect: "none",
                                textAlign: 'center'
                            }}
                        >
                            All
                        </Box>
                    </Grid>
                    {visibleCategories.map((c, index) => (
                        <Grid item xs={2} key={c._id}>
                            <Box
                                key={c._id}
                                onClick={() => setSelectedCategory(c._id)}
                                sx={{
                                    cursor: "pointer",
                                    whiteSpace: "nowrap",
                                    padding: "8px 14px",
                                    borderRadius: "999px",
                                    backgroundColor: selectedCategory === c._id ? "#000" : "#fff",
                                    color: selectedCategory === c._id ? "#fff" : "#000",
                                    border: "1px solid #ddd",
                                    fontSize: "13px",
                                    userSelect: "none",
                                    textAlign: 'center'
                                }}
                            >
                                {c.name}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Grid>

            {/* Products Grid */}
            <Grid container justifyContent={'center'}>
                {loading ? (
                    <Grid item md={3} sx={{ margin: "30px", textAlign: "center" }}>
                        <CircularProgress sx={{ color: "#000", margin: "10px 0 15px 0" }} />
                        <Typography>Fetching Products...</Typography>
                    </Grid>
                ) : filteredProducts.length === 0 ? (
                    <Typography sx={{ textAlign: "center", fontSize: "16px", margin: "20px" }}>
                        No products found.
                    </Typography>
                ) : (
                    filteredProducts.map((product) => (
                        <Grid
                            item
                            md={3}
                            key={product._id}
                            sx={{
                                backgroundColor: product?.isExhausted
                                    ? "#FEE2E2"
                                    : product?.isFreebieProduct
                                        ? "#F9FFFC"
                                        : "#ffffff",
                                margin: "10px",
                                textAlign: "center",
                                padding: "18px 12px",
                                borderRadius: "14px",
                                position: "relative",
                                transition: "all 0.25s ease",
                                boxShadow: "0 2px 10px rgba(0,0,0,0.04)",

                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 8px 22px rgba(0,0,0,0.08)",
                                },
                            }}
                        >
                            {product?.isFreebieProduct ? (
                                <Chip
                                    label="🎁 FREEBIE"
                                    size="small"
                                    sx={{
                                        position: "absolute",
                                        top: 10,
                                        left: 10,
                                        fontWeight: 900,
                                        fontSize: "11px",
                                        backgroundColor: "#10B981",
                                        color: "#fff",
                                        borderRadius: "6px",
                                        letterSpacing: "0.5px",
                                        "& .MuiChip-label": { px: 1 },
                                    }}
                                />
                            ) : (
                                product?.isExhausted && (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 10,
                                            left: 10,
                                            fontSize: "14px",
                                        }}
                                    >
                                        ⚠️
                                    </Box>
                                )
                            )}

                            <IconButton
                                disabled={!!updatingProductId}
                                sx={{
                                    position: "absolute",
                                    top: 6,
                                    right: 6,
                                    opacity: updatingProductId && updatingProductId !== product._id ? 0.4 : 1,
                                }}
                                onClick={() => handleSwitchChange(product._id, !product.isExhausted)}
                            >
                                {updatingProductId === product._id ? (
                                    <CircularProgress size={24} sx={{ color: "#000" }} />
                                ) : product.isExhausted ? (
                                    <ToggleOnOutlined style={{ fontSize: "28px", color: "red" }} />
                                ) : (
                                    <ToggleOffOutlined style={{ fontSize: "28px", color: "green" }} />
                                )}
                            </IconButton>

                            <img
                                src={product?.displayImageUrl || Images?.product}
                                alt={product?.name}
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "170px",
                                    objectFit: "contain",
                                }}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = Images?.product;
                                }}
                            />

                            <Typography
                                sx={{
                                    fontSize: "14px",
                                    fontWeight: 700,
                                    marginTop: "10px",
                                    color: "#0f172a",
                                }}
                            >
                                {product?.name}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    marginTop: "6px",
                                    fontWeight: 600,
                                    color: product?.isExhausted ? "#DC2626" : "#16a34a",
                                }}
                            >
                                {product?.isExhausted ? "Out of Stock" : "In Stock"}
                            </Typography>
                        </Grid>
                    ))
                )}
            </Grid>
        </Grid>
    );
};

export default ProductsListing;
